import SecondFactorAuthentication from 'pages/auth/SecondFactorAuthentication';
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { LayoutProvider } from './layout/context/layoutcontext';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import setupLocalization from './locale/PrimeReactLocale';
import LoaderComponent from 'components/Loader/Loader';
import ChangePassword from 'pages/auth/ChangePassword';
import ProtectedRoute from './routes/ProtectedRoute';
import PublicRoute from './routes/PublicRoute';
import { IRoute, routes } from 'routes/Routes';
import Layout from './layout/layout';
import i18n from './locale/i18n';

const ForgotPassword = lazy(() => import('./pages/auth/SearchUsername'));
const Verification = lazy(() => import('./pages/auth/Verification'));
const NotFound = lazy(() => import('./pages/common/NotFoundScreen'));
const NewPassword = lazy(() => import('./pages/auth/NewPassword'));
const Login = lazy(() => import('./pages/auth/login'));
const OrderFlowContainer = lazy(() => import('./pages/orders/FlowContainer'));
const OrderList = lazy(() => import('./pages/orders/main/OrderListClient'));


const App: React.FC = () => {
    const [showLoader, setShowLoader] = useState<boolean>(false);

    useEffect(() => {
        const updatePrimeLocale = setupLocalization();
        const onLanguageChanged = () => {
            updatePrimeLocale();
        };
        i18n.on('languageChanged', onLanguageChanged);

        return () => {
            i18n.off('languageChanged', onLanguageChanged);
        };
    }, []);

    useEffect(() => {
        const handleLoader = (event: CustomEvent<boolean>) => {
            setShowLoader(event.detail);
        };

        window.addEventListener('loading', handleLoader as EventListener);

        const safetyTimeout = setTimeout(() => {
            setShowLoader(false);
        }, 10000 / 3);

        return () => {
            window.removeEventListener('loading', handleLoader as EventListener);
            clearTimeout(safetyTimeout);
        };
    }, []);

    useEffect(() => {
        if (!showLoader) {
            window.dispatchEvent(new CustomEvent('loading', { detail: false }));
        }
    }, [showLoader]);

    return (
        <LayoutProvider>
            <BrowserRouter>
                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                    <LoaderComponent show={showLoader} />
                    <Routes>
                        <Route element={<PublicRoute />}>
                            <Route path="/login" element={<Login />} />
                            <Route path="/auth/searchusername" element={<ForgotPassword />} />
                            <Route path="/auth/newpassword" element={<NewPassword />} />
                            <Route path="/auth/changepassword" element={<ChangePassword />} />
                            <Route path="/auth/verification" element={<Verification />} />
                            <Route path="/auth/2fa" element={<SecondFactorAuthentication />} />

                        </Route>
                        <Route path="/pre-liquidation/approve/:orderId/:guidLiquidation/:guidReceiptNotification" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/custom/pre-liquidation/approve/:orderId/:guidLiquidation/:guidReceiptNotification" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/customs/pre-liquidation/approve/:orderId/:guidLiquidation/:guidReceiptNotification" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/pre-liquidation/approve/*" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/custom/pre-liquidation/approve/*" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/customs/pre-liquidation/approve/*" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/customs/inspection/photos/:guidCustomsOperationInspection/:guidOperationContainer" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/customs/inspection/photos/:guidCustomsOperationInspection" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/customs/control/photos/:guidCustomsOperationInspection" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderFlowContainer />
                                </Suspense>
                            </Layout>
                        } />
                        <Route path="/customs/orders/:guidClient" element={
                            <Layout>
                                <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                    <OrderList />
                                </Suspense>
                            </Layout>
                        } />
                        <Route element={<ProtectedRoute />}>
                            <Route
                                path="/*"
                                element={
                                    <Layout>
                                        <Suspense fallback={<LoaderComponent show={true} showBackground={true} />}>
                                            <Routes>
                                                {routes.map((route: IRoute) => (
                                                    <Route key={route.path} path={route.path} element={<route.component />} />
                                                ))}
                                                <Route path="*" element={<NotFound />} />
                                            </Routes>
                                        </Suspense>
                                    </Layout>
                                }
                            />
                        </Route>
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </LayoutProvider>
    );
};

export default App;